import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon.jsx';
import { APP_CONFIG } from '../config/appConfig.js';
import { calculateResults, formatCurrency, formatNumber } from '../utils/calculations.js';

const { FiBookOpen, FiChevronDown, FiChevronUp } = FiIcons;

const WorkedExample = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const exampleInputs = APP_CONFIG.exampleValues;
  const exampleResults = calculateResults(exampleInputs);

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-8 py-6 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-4 focus:ring-violet-200"
            aria-expanded={isOpen}
            aria-controls="worked-example-content"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SafeIcon 
                  icon={FiBookOpen} 
                  className="text-2xl text-violet-600" 
                  aria-hidden="true"
                />
                <h2 className="text-2xl font-bold text-gray-900">
                  Worked Example
                </h2>
              </div>
              <SafeIcon 
                icon={isOpen ? FiChevronUp : FiChevronDown} 
                className="text-xl text-gray-500" 
                aria-hidden="true"
              />
            </div>
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                id="worked-example-content"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-gray-200"
              >
                <div className="px-8 py-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Inputs */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Example Scenario
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Monthly diesel used:</span>
                          <span className="font-medium">{formatNumber(exampleInputs.litres)} litres</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Diesel price per litre:</span>
                          <span className="font-medium">{formatCurrency(exampleInputs.pricePerLitre)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Generator hours per month:</span>
                          <span className="font-medium">{formatNumber(exampleInputs.hours)} hours</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Average load:</span>
                          <span className="font-medium">{formatNumber(exampleInputs.avgLoad)} kW</span>
                        </div>
                      </div>
                    </div>

                    {/* Results */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Calculated Results
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Monthly diesel spend:</span>
                          <span className="font-medium">{formatCurrency(exampleResults.monthlySpend)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Estimated kWh generated:</span>
                          <span className="font-medium">{formatNumber(exampleResults.kWhGenerated)} kWh</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-gray-600">Fuel-only cost per kWh:</span>
                          <span className="font-medium text-violet-600">{formatCurrency(exampleResults.fuelCostPerKwh, 3)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-600">All-in estimate range:</span>
                          <span className="font-medium">{formatCurrency(exampleResults.allInLow, 3)} - {formatCurrency(exampleResults.allInHigh, 3)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Calculation Steps */}
                  <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">How we calculated this:</h4>
                    <ol className="space-y-2 text-sm text-gray-700">
                      <li>
                        <strong>1. Monthly spend:</strong> {formatNumber(exampleInputs.litres)} litres × {formatCurrency(exampleInputs.pricePerLitre)} = {formatCurrency(exampleResults.monthlySpend)}
                      </li>
                      <li>
                        <strong>2. kWh generated:</strong> {formatNumber(exampleInputs.avgLoad)} kW × {formatNumber(exampleInputs.hours)} hours = {formatNumber(exampleResults.kWhGenerated)} kWh
                      </li>
                      <li>
                        <strong>3. Cost per kWh:</strong> {formatCurrency(exampleResults.monthlySpend)} ÷ {formatNumber(exampleResults.kWhGenerated)} kWh = {formatCurrency(exampleResults.fuelCostPerKwh, 3)} per kWh
                      </li>
                    </ol>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default WorkedExample;